/* oxlint-disable @typescript-eslint/no-non-null-assertion */
import { IS_MAC } from '@blocksuite/hive/global/env';
import {
  Bound,
  getCommonBoundWithRotation,
} from '@blocksuite/hive/global/gfx';
import type { PointerEventState } from '@blocksuite/hive/std';
import {
  BaseTool,
  type GfxModel,
  MouseButton,
} from '@blocksuite/hive/std/gfx';
import { Subject } from 'rxjs';

import {
  HIVE_AI_PANEL_WIDGET,
  type HiveAIPanelWidget,
} from '../widgets/ai-panel/ai-panel.js';

export class CopilotTool extends BaseTool {
  static override toolName: string = 'copilot';

  private _dragging = false;

  // eslint-disable-next-line rxjs/finnish
  draggingAreaUpdated = new Subject<boolean | void>();

  dragLastPoint: [number, number] = [0, 0];

  dragStartPoint: [number, number] = [0, 0];

  override get allowDragWithRightButton() {
    return true;
  }

  get area() {
    const start = new DOMPoint(this.dragStartPoint[0], this.dragStartPoint[1]);
    const end = new DOMPoint(this.dragLastPoint[0], this.dragLastPoint[1]);

    const minX = Math.min(start.x, end.x);
    const minY = Math.min(start.y, end.y);
    const maxX = Math.max(start.x, end.x);
    const maxY = Math.max(start.y, end.y);

    return new DOMRect(minX, minY, maxX - minX, maxY - minY);
  }

  // AI processing
  get processing() {
    const aiPanel = this.gfx.std.view.getWidget(
      HIVE_AI_PANEL_WIDGET,
      this.doc.root!.id
    ) as HiveAIPanelWidget;
    return aiPanel && aiPanel.state !== 'hidden';
  }

  get selectedElements() {
    return this.gfx.selection.selectedElements;
  }

  private _initDragState(e: PointerEventState) {
    this.dragStartPoint = this.gfx.viewport.toModelCoord(e.x, e.y);
    this.dragLastPoint = this.dragStartPoint;
  }

  abort() {
    this._dragging = false;
    this.dragStartPoint = [0, 0];
    this.dragLastPoint = [0, 0];
    this.gfx.tool.setTool('default');
  }

  override activate(): void {
    this.gfx.viewport.locked = true;

    if (this.gfx.selection.lastSurfaceSelections) {
      this.gfx.selection.set(this.gfx.selection.lastSurfaceSelections);
    }
  }

  override deactivate(): void {
    this.gfx.viewport.locked = false;
  }

  override dragEnd(): void {
    if (!this._dragging) return;

    this._dragging = false;
    this.draggingAreaUpdated.next(true);
  }

  override dragMove(e: PointerEventState): void {
    if (!this._dragging) return;

    this.dragLastPoint = this.gfx.viewport.toModelCoord(e.x, e.y);

    const area = this.area;
    const bound = new Bound(area.x, area.y, area.width, area.height);

    if (area.width & area.height) {
      const elements = this.gfx.getElementsByBound(bound);

      const set = new Set(elements);

      this.gfx.selection.set({
        elements: Array.from(set).map(element => element.id),
        editing: false,
        inoperable: true,
      });
    }

    this.draggingAreaUpdated.next();
  }

  override dragStart(e: PointerEventState): void {
    if (this.processing) return;

    this._initDragState(e);
    this._dragging = true;
    this.draggingAreaUpdated.next();
  }

  override mounted(): void {
    this.addHook('pointerDown', evt => {
      const useCopilot =
        evt.raw.button === MouseButton.SECONDARY ||
        (evt.raw.button === MouseButton.MAIN && IS_MAC
          ? evt.raw.metaKey
          : evt.raw.ctrlKey);

      if (useCopilot) {
        this.controller.setTool('copilot');
        return false;
      }

      return;
    });
  }

  override pointerDown(e: PointerEventState): void {
    if (this.processing) {
      e.raw.stopPropagation();
      return;
    }

    this.gfx.tool.setTool('default');
  }

  updateDragPointsWith(selectedElements: GfxModel[], padding = 0) {
    const bounds = getCommonBoundWithRotation(selectedElements).expand(
      padding / this.gfx.viewport.zoom
    );

    this.dragStartPoint = bounds.tl as [number, number];
    this.dragLastPoint = bounds.br as [number, number];
  }

  updateSelectionWith(selectedElements: GfxModel[], padding = 0) {
    const { selection } = this.gfx;

    selection.clear();

    this.updateDragPointsWith(selectedElements, padding);

    selection.set({
      elements: selectedElements.map(e => e.id),
      editing: false,
      inoperable: true,
    });

    this.draggingAreaUpdated.next(true);
  }
}

declare module '@blocksuite/std/gfx' {
  interface GfxToolsMap {
    copilot: CopilotTool;
  }
}
